import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User } from '@auth/domain/entities/User';

interface ProfileDropdownProps {
  user: User | null;
  logout: () => void;
  settingsPath: string;
  /** Muestra la opción "Tutoriales" en el dropdown (cuando el botón del header ya no aplica). */
  showTutorialsItem?: boolean;
  /** Callback al pulsar "Tutoriales" (abre el menú de tutoriales). */
  onOpenTutorials?: () => void;
}

function getInitials(user: User | null): string {
  const first = user?.name?.[0];
  const second = user?.surname?.[0];
  if (first && second) return `${first}${second}`.toUpperCase();
  if (first) return first.toUpperCase();
  return (user?.email?.[0] ?? 'U').toUpperCase();
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ user, logout, settingsPath, showTutorialsItem, onOpenTutorials }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const displayName = user?.name?.split(' ')[0] ?? user?.email?.split('@')[0] ?? 'Usuario';
  const ROLE_LABELS: Record<string, string> = { superadmin: 'Superadmin', admin: 'Admin', backupmanager: 'Backup Manager' };
  const displayRole = user?.role ? (ROLE_LABELS[user.role] ?? user.role) : '';
  const initials = getInitials(user);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleProfileClick = () => {
    setIsOpen(false);
    navigate(settingsPath);
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  const handleOpenTutorials = () => {
    setIsOpen(false);
    onOpenTutorials?.();
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        aria-label={`${initials} ${displayName}`}
        className="flex items-center gap-3 cursor-pointer group outline-none border-none bg-transparent"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="text-right hidden md:block">
          <p className="text-sm font-black text-klonup-indigo group-hover:text-klonup-sky transition-colors leading-none">
            {displayName}
          </p>
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest leading-none mt-1">
            {displayRole}
          </p>
        </div>
        {user?.photoUrl ? (
          <img
            src={user.photoUrl}
            alt={displayName}
            className="w-11 h-11 rounded-full border border-indigo-100 shadow-sm object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div
            className="w-11 h-11 rounded-full border border-indigo-100 shadow-sm flex items-center justify-center text-sm font-bold bg-gray-100 text-gray-700 group-hover:bg-klonup-indigo group-hover:text-white transition-colors"
          >
            {initials}
          </div>
        )}
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '110%',
            minWidth: '160px',
            backgroundColor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
            zIndex: 200,
            overflow: 'hidden',
          }}
        >
          <button onClick={handleProfileClick} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.875rem', color: '#111827' }} className="hover:bg-gray-50 transition-colors">
            {t('profile_menu.my_profile')}
          </button>
          {showTutorialsItem && (
            <button onClick={handleOpenTutorials} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.875rem', color: '#111827' }} className="hover:bg-gray-50 transition-colors">
              {t('profile_menu.tutorials')}
            </button>
          )}
          <button onClick={handleLogout} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.875rem', color: '#DC2626' }} className="hover:bg-red-50 transition-colors">
            {t('profile_menu.logout')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
